'use client'

import { useSession } from 'next-auth/react'
import { useEffect } from 'react'
import useSWR from 'swr'
import posthog from 'posthog-js'
import { UserRole } from '@/lib/generated/prisma/browser'
import { applyPosthogSupportIdentity, posthogAdditionalDetailsKey } from '@/lib/posthog/apply-posthog-support-identity'

const PERSON_PROPERTY_EXCLUDES = new Set(['identityHash', 'distinctId'])

/** Keys read by `contentMonsterAnalyticsContext()` via `posthog.get_property`. */
const CONTENT_MONSTER_SUPER_PROPERTY_KEYS = [
  'primaryFacilityName',
  'primaryFacilityId',
  'accountId',
  'accountName',
] as const

function syncContentMonsterSuperProperties(props: {
  primaryFacilityName?: string | null
  primaryFacilityId?: number | null
  accountId?: string | null
  accountName?: string | null
}) {
  const toRegister: Record<string, string | number> = {}
  for (const key of CONTENT_MONSTER_SUPER_PROPERTY_KEYS) {
    const value = props[key]
    if (value != null && value !== '') {
      toRegister[key] = value
    } else {
      posthog.unregister(key)
    }
  }
  if (Object.keys(toRegister).length > 0) {
    posthog.register(toRegister)
  }
}

function onlyDefined(details: Record<string, unknown> | undefined): Record<string, string> {
  if (!details) return {}
  return Object.fromEntries(
    Object.entries(details).filter(
      ([key, value]) => !PERSON_PROPERTY_EXCLUDES.has(key) && value != null && value !== '',
    ) as [string, string][],
  )
}

export default function PostHogIdentify() {
  const { data: session } = useSession()
  const impersonating = Boolean(session?.user?.isSuperAdmin && session.user.role !== UserRole.GRATIA_ADMIN)
  const shouldFetch = Boolean(session?.user) && !impersonating
  const { data: additionalDetails } = useSWR(
    shouldFetch ? posthogAdditionalDetailsKey(session!.user.id) : null,
    applyPosthogSupportIdentity,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    },
  )

  useEffect(() => {
    if (!session?.user) return
    if (impersonating) {
      console.log('Impersonating user, no posthog tracking', session.user.id)
      posthog.clearIdentity()
      for (const key of CONTENT_MONSTER_SUPER_PROPERTY_KEYS) {
        posthog.unregister(key)
      }
      return
    }

    const fromDetails = onlyDefined(additionalDetails)

    posthog.identify(session.user.id, {
      email: session.user.email,
      name: session.user.name,
      role: session.user.role,
      isSuperAdmin: session.user.isSuperAdmin,
      accountId: session.user.accountId,
      accountName: additionalDetails?.accountName,
      primaryFacilityId: additionalDetails?.primaryFacilityId ?? undefined,
      primaryFacilityName: additionalDetails?.primaryFacilityName ?? undefined,
      facilityNames: additionalDetails?.facilityNames ?? undefined,
      facilityIds: session.user.facilityIds,
      participantId: session.user.participantId,
      participantEmploymentStatus: session.user.participantEmploymentStatus,
      participantShiftType: session.user.participantShiftType,
      ...fromDetails,
    })

    syncContentMonsterSuperProperties({
      accountId: session.user.accountId,
      accountName: additionalDetails?.accountName,
      primaryFacilityId: additionalDetails?.primaryFacilityId,
      primaryFacilityName: additionalDetails?.primaryFacilityName,
    })
  }, [session, session?.user, additionalDetails, impersonating])

  return null
}
